// src/components/results/storyText.js
//
// Turns resultInsights output + taxonomy into what the student reads: labels in the
// UI language, the "why" for each mistake, the question text, and practice links.
// Taxonomy text exists in German and English; other UI languages get English.

const SECTION_IDS = { lesen: ['reading', 'lesen'], sprachbausteine: ['grammar', 'sprachbausteine'], hoeren: ['listening', 'hören', 'hoeren'], schreiben: ['writing', 'schreiben'] };

/** Question ids repeat across parts (Lesen q1 and Hören q1), so items are keyed by part too. */
export const itemKey = (it) => `${it.partId}/${it.questionId}`;

export const pick = (bi, lang) => {
  if (!bi) return '';
  if (typeof bi === 'string') return bi;
  return (lang === 'de' ? bi.de : bi.en) || bi.en || bi.de || '';
};

/** taxonomy entry for "family" or "family.fine" of any kind */
export function indexTaxonomy(tax) {
  const byKind = { skill: {}, trap: {}, language: {}, point: {}, reason: {} };
  (tax?.skills || []).forEach((e) => { byKind.skill[e.id] = e; });
  (tax?.traps || []).forEach((e) => { byKind.trap[e.id] = e; });
  (tax?.languageFamilies || []).forEach((e) => { byKind.language[e.id] = e; });
  (tax?.languagePoints || []).forEach((e) => { byKind.point[e.id] = e; });
  (tax?.grammarReasons || []).forEach((e) => { byKind.reason[e.id] = e; });
  return byKind;
}

const fineOf = (entry, dotted) => {
  const fine = String(dotted || '').split('.')[1];
  return fine ? (entry?.fine || []).find((f) => f.id === fine) : null;
};

export const familyEntry = (idx, kind, id) => idx[kind]?.[String(id || '').split('.')[0]] || null;

/** Label of a family (with its fine label in brackets when useful). */
export function labelOf(idx, kind, id, lang) {
  const entry = familyEntry(idx, kind, id);
  return entry ? pick(entry.label, lang) : '';
}

/** Section score from the page's authoritative sectionScores (includes Schreiben). */
export function sectionScore(sectionScores, key) {
  for (const id of SECTION_IDS[key] || []) {
    const s = sectionScores?.[id];
    if (s && s.score != null) return { score: Number(s.score), max: Number(s.maxScore ?? s.total ?? 0) };
  }
  return null;
}

/** The sentence around gap n in a Sprachbausteine text, with the gap as ___. */
export function gapSentence(text, n) {
  if (!text) return '';
  const marker = new RegExp(`(?:_+\\s*)?\\(${n}\\)|\\[${n}\\]`);   // telc "___ (17)" or "(31)", Goethe "[17]"
  const m = text.match(marker);
  if (!m) return '';
  const i = m.index;
  const start = Math.max(text.lastIndexOf('.', i - 1), text.lastIndexOf('!', i - 1), text.lastIndexOf('?', i - 1), text.lastIndexOf('\n', i - 1)) + 1;
  const endIdx = text.slice(i + m[0].length).search(/[.!?\n]/);
  const end = endIdx < 0 ? text.length : i + m[0].length + endIdx + 1;
  return text.slice(start, end).replace(m[0], '___').replace(/(?:_+\s*)?\(\d{1,2}\)|\[\d{1,2}\]/g, '…').replace(/\s+/g, ' ').trim();
}

/** What the student was asked, in one line. */
export function questionText(q, part) {
  if (q.statement) return q.statement;
  if (q.questionText) return q.questionText;
  const c = part.content || {};
  if (part.partId === 'reading-part-3') {
    const s = (c.situations || []).find((x) => String(x.id) === String(q.questionNumber));
    if (s) return s.text;
  }
  if (part.partId === 'reading-part-1') return null;   // the page shows "Text n" instead
  if (c.text && q.blankNumber != null) return gapSentence(c.text, q.blankNumber);
  return '';
}

/**
 * Why this answer was wrong: { trap, why, tip, kind } for the page. `t` is i18next's t.
 */
export function explainCause(item, idx, lang, t) {
  const c = item.cause || {};
  if (c.kind === 'trap' && c.trap) {
    const entry = familyEntry(idx, 'trap', c.trap);
    return {
      label: pick(entry?.label, lang),
      why: pick(c.why, lang) || pick(entry?.what, lang),
      tip: pick(entry?.tip, lang),
    };
  }
  if (c.kind === 'reason' && c.reason) {
    const entry = familyEntry(idx, 'reason', c.reason);
    const point = idx.point[item.point];
    return {
      label: pick(entry?.label, lang),
      why: [pick(fineOf(entry, c.reason)?.label, lang), pick(entry?.what, lang)].filter(Boolean).join(' — '),
      tip: pick(point?.tip, lang) || pick(entry?.tip, lang),
    };
  }
  if (c.kind === 'point') {
    const point = idx.point[item.point];
    return { label: pick(point?.label, lang), why: pick(point?.example ? { de: point.example, en: point.example } : null, lang), tip: pick(point?.tip, lang) };
  }
  if (c.kind === 'other-key') return { label: t('examResults.story.cause.otherKeyLabel'), why: t('examResults.story.cause.otherKey', { n: c.questionId?.replace(/^q/, '') }), tip: '' };
  if (c.kind === 'unrelated') return { label: t('examResults.story.cause.unrelatedLabel'), why: pick(c.gist, lang) ? t('examResults.story.cause.unrelated', { gist: pick(c.gist, lang) }) : '', tip: '' };
  if (c.kind === 'slip') return { label: t('examResults.story.cause.slipLabel'), why: t('examResults.story.cause.slip'), tip: '' };
  if (c.kind === 'skipped') return { label: t('examResults.story.cause.skippedLabel'), why: t('examResults.story.cause.skipped'), tip: '' };
  return { label: '', why: '', tip: '' };
}

/**
 * Practice targets for a family/point at this level → [{ kind, to, state, premium, label }].
 * Grammar pages are public (free); exercises and lessons are Premium. `section` (optional)
 * keeps strategy lessons to that exam part.
 */
export function practiceLinks(entry, level, provider, t, section) {
  const lvl = String(level || '').toLowerCase();
  // A skill shared by Lesen and Hören lists both strategy lessons; keep the one for this part.
  const prefix = { lesen: 'lv_', hoeren: 'hv_' }[section];
  // ÖSD B1 lessons follow the newer ÖSD format; only Lesen 1–3 match our (ZDÖ-format) papers.
  const osdMismatch = (p) => String(provider).toLowerCase() === 'osd' && p.kind === 'strategy-lesson' && !['lv_part1', 'lv_part2', 'lv_part3'].includes(p.file);
  const list = (entry?.practice?.[lvl] || []).filter((p) => (p.kind !== 'strategy-lesson' || !prefix || String(p.file).startsWith(prefix)) && !osdMismatch(p));
  return list.map((p) => {
    const at = String(p.level || lvl).toLowerCase();
    if (p.kind === 'grammar-page') return { to: `/grammar/${at}/${p.slug}`, premium: false, label: at !== lvl ? t('examResults.story.practice.basics') : t('examResults.story.practice.rule') };
    if (p.kind === 'grammar-practice') return { to: `/practice/grammar?pillar=${p.pillar}${at !== lvl ? `&level=${at}` : ''}`, premium: true, label: t('examResults.story.practice.exercises') };
    if (p.kind === 'strategy-lesson') return { to: '/lesson-strategy', state: { dataUrl: `/data/lessons/${String(provider).toLowerCase()}-${lvl}/${p.file}.json` }, premium: true, label: t('examResults.story.practice.strategy') };
    return null;
  }).filter(Boolean)
    .filter((l, i, all) => all.findIndex((x) => x.label === l.label) === i)   // one button per kind
    .slice(0, 2);
}

/** Short margin note: the teacher writes the right answer next to the mistake. */
export function marginNote(item, q, part, formatAnswer) {
  if (item.correct) return '✓';
  const right = String(formatAnswer(q.correctAnswer, q, part) || '');
  // A teacher only writes short corrections in the margin; long answers are printed anyway.
  return right && right.length <= 16 ? `→ ${right}` : null;
}

/**
 * The answer options shown in the question panel: every option for verdict and
 * multiple-choice items, otherwise just the student's choice and the right one.
 * → [{ value, label, isKey, isYours }]
 */
export function panelOptions(item, q, part, formatAnswer, isAnswerCorrect) {
  let values;
  if (typeof q.correctAnswer === 'boolean') values = [true, false];
  else if (q.options?.length) values = q.options.map((o) => o.id);
  else {
    // Headings, ads and typed gaps: the student's answer (when wrong) and the key. A typed
    // key can list several accepted words, so it stays whole: "Es / Dieses / Das".
    const key = { value: String(q.correctAnswer).toLowerCase(), label: String(formatAnswer(q.correctAnswer, q, part) || ''), isKey: true, isYours: item.correct };
    if (item.correct || !item.answered) return [key];
    return [{ value: String(item.answer).toLowerCase(), label: String(formatAnswer(item.answer, q, part) || item.answer), isKey: false, isYours: true }, key];
  }
  return values.map((value) => ({
    value,
    label: String(formatAnswer(value, q, part) || value),
    isKey: isAnswerCorrect(value, q, part),
    isYours: item.answered && isAnswerCorrect(item.answer, { ...q, correctAnswer: value }, part),
  }));
}

/** What a wrong answer is grouped under in "What fooled you here": the trap, else the language family. */
export function causeGroup(item, idx, lang, t) {
  const c = item.cause || {};
  if (c.kind === 'trap' && c.trap) {
    const e = familyEntry(idx, 'trap', c.trap);
    return { key: `trap:${e?.id || c.trap}`, label: pick(e?.label, lang), tip: pick(e?.tip, lang) };
  }
  if ((c.kind === 'reason' || c.kind === 'point') && item.languageFamily) {
    const e = familyEntry(idx, 'language', item.languageFamily);
    return { key: `language:${item.languageFamily}`, label: pick(e?.label, lang), tip: pick(e?.tip, lang) || pick(idx.point[item.point]?.tip, lang) };
  }
  const why = explainCause(item, idx, lang, t);
  return why.label ? { key: `cause:${c.kind}`, label: why.label, tip: why.why } : null;
}

/** Points with one decimal, German style ("22,5"). */
export const fmt = (v) => (Math.round(Number(v) * 10) / 10).toString().replace('.', ',');

/** A hand-drawn ink line from 0 to `ratio` of `width`, wobbling gently around y. */
export function inkPath(ratio, width, y, step = 40) {
  const end = Math.max(4, Math.round(Math.min(1, Math.max(0, ratio)) * width));
  let d = `M4 ${y}`; let up = true;
  for (let x = step; x < end; x += step) { d += ` Q ${x - step / 2} ${up ? y - 2.5 : y + 2.5} ${x} ${y}`; up = !up; }
  return `${d} L ${end} ${y}`;
}
